// frontend/src/infrastructure/audio/VoiceActivityDetector.ts
//
// Detekcja ciszy (VAD — Voice Activity Detection) dla toru PTT w trybie Toggle.
// Po starcie nagrywania nikt nie trzyma już przycisku, więc sesję musi domknąć
// sam system: jeśli przez CIĄGŁE `silenceMs` (domyślnie 10 s) poziom sygnału z
// mikrofonu nie przekroczy progu, `onSilence()` mówi konsumentowi „zamknij
// nagranie i zresetuj mostek".
//
// Warstwa infrastruktury: cała rozmowa z `AudioContext` / `AnalyserNode` żyje
// tutaj. Zależności (zegar, pętla klatek, fabryka `AudioContext`) są
// wstrzykiwalne — w testach atrapy, w produkcji realne API przeglądarki.
// Brak `AudioContext` (część WebView) NIE jest błędem krytycznym: VAD po prostu
// się nie uruchamia, a tor nagrywania działa dalej (bez watchdogu ciszy).

/** Domyślna długość ciągłej ciszy wyzwalająca auto-stop — „VAD - 10s". */
export const DEFAULT_SILENCE_MS = 10_000
/** Domyślny próg RMS (0..1) poniżej którego ramkę uznajemy za ciszę. */
export const DEFAULT_SILENCE_THRESHOLD = 0.015

export interface VoiceActivityDetectorOptions {
  /** Strumień z mikrofonu do analizy poziomu głośności. */
  stream: MediaStream
  /** Wołane DOKŁADNIE RAZ, gdy cisza utrzyma się nieprzerwanie przez `silenceMs`. */
  onSilence: () => void
  /** Długość ciągłej ciszy wyzwalająca `onSilence`. Domyślnie {@link DEFAULT_SILENCE_MS}. */
  silenceMs?: number
  /** Próg RMS uznania ramki za ciszę. Domyślnie {@link DEFAULT_SILENCE_THRESHOLD}. */
  threshold?: number
  /** Źródło czasu (ms). Domyślnie `performance.now()`. */
  now?: () => number
  /** Rejestruje wywołanie zwrotne na kolejną klatkę; zwraca uchwyt. Domyślnie `requestAnimationFrame`. */
  requestFrame?: (callback: () => void) => number
  /** Anuluje zaplanowaną klatkę. Domyślnie `cancelAnimationFrame`. */
  cancelFrame?: (handle: number) => void
  /** Fabryka `AudioContext`. Domyślnie `window.AudioContext` / `webkitAudioContext`. */
  createAudioContext?: () => AudioContext
}

function defaultCreateAudioContext(): AudioContext {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) throw new Error('AudioContext niedostępny')
  return new Ctor()
}

/**
 * RMS (0..1) z bufora czasowego `getByteTimeDomainData` (bajty wokół 128).
 * `buffer` jest zawsze niepusty (rozmiar = `fftSize`), więc bez strażnika dzielenia.
 */
export function computeRms(buffer: Uint8Array): number {
  let sumSquares = 0
  for (let i = 0; i < buffer.length; i++) {
    const centered = (buffer[i] - 128) / 128
    sumSquares += centered * centered
  }
  return Math.sqrt(sumSquares / buffer.length)
}

export class VoiceActivityDetector {
  private readonly stream: MediaStream
  private readonly onSilence: () => void
  private readonly silenceMs: number
  private readonly threshold: number
  private readonly now: () => number
  private readonly requestFrame: (callback: () => void) => number
  private readonly cancelFrame: (handle: number) => void
  private readonly createAudioContext: () => AudioContext

  private context: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private buffer = new Uint8Array(0)
  private frameHandle: number | null = null
  private silenceStartedAt = 0
  private fired = false
  private running = false

  constructor(options: VoiceActivityDetectorOptions) {
    this.stream = options.stream
    this.onSilence = options.onSilence
    this.silenceMs = options.silenceMs ?? DEFAULT_SILENCE_MS
    this.threshold = options.threshold ?? DEFAULT_SILENCE_THRESHOLD
    this.now = options.now ?? (() => performance.now())
    this.requestFrame = options.requestFrame ?? ((callback) => requestAnimationFrame(callback))
    this.cancelFrame = options.cancelFrame ?? ((handle) => cancelAnimationFrame(handle))
    this.createAudioContext = options.createAudioContext ?? defaultCreateAudioContext
  }

  /** Buduje graf analizy i rusza pętlę klatek. No-op, gdy już działa. */
  start(): void {
    if (this.running) return
    this.running = true
    this.fired = false
    this.silenceStartedAt = this.now()

    try {
      this.context = this.createAudioContext()
      this.analyser = this.context.createAnalyser()
      this.analyser.fftSize = 2048
      this.buffer = new Uint8Array(this.analyser.fftSize)
      this.source = this.context.createMediaStreamSource(this.stream)
      this.source.connect(this.analyser)
    } catch (error) {
      // Bez AudioContext watchdog ciszy jest wyłączony — tor nagrywania trwa dalej.
      console.warn('[VoiceActivityDetector] brak analizy audio — VAD wyłączony:', error)
      this.running = false
      this.teardownGraph()
      return
    }

    this.scheduleFrame()
  }

  /** Zatrzymuje pętlę, odpina graf i zamyka `AudioContext`. Idempotentne. */
  stop(): void {
    this.running = false
    if (this.frameHandle !== null) {
      this.cancelFrame(this.frameHandle)
      this.frameHandle = null
    }
    this.teardownGraph()
  }

  private teardownGraph(): void {
    try {
      this.source?.disconnect()
    } catch {
      // graf mógł być już odpięty — nieistotne
    }
    this.source = null
    this.analyser = null
    const ctx = this.context
    this.context = null
    if (ctx) {
      try {
        void ctx.close()
      } catch {
        // AudioContext mógł być już zamknięty — nieistotne
      }
    }
  }

  private scheduleFrame(): void {
    this.frameHandle = this.requestFrame(this.tick)
  }

  private readonly tick = (): void => {
    // `running` jest zdejmowane w `stop()` PRZED wyzerowaniem grafu, a graf
    // powstaje w `start()` przed pierwszą klatką — więc gdy tu wchodzimy z
    // `running === true`, `analyser` na pewno istnieje.
    if (!this.running) return
    const analyser = this.analyser as AnalyserNode

    analyser.getByteTimeDomainData(this.buffer)
    const rms = computeRms(this.buffer)
    const t = this.now()

    if (rms >= this.threshold) {
      // Był dźwięk — licznik ciszy startuje od zera.
      this.silenceStartedAt = t
    } else if (t - this.silenceStartedAt >= this.silenceMs && !this.fired) {
      this.fired = true
      this.stop()
      this.onSilence()
      return
    }

    this.scheduleFrame()
  }
}
